import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { Button } from './Button'

/**
 * Ngăn xếp các modal đang mở, theo đúng thứ tự mở. Cấp module vì hai chuyện dưới đây là
 * **toàn cục**, không thuộc về riêng một instance:
 *
 * 1. Escape chỉ được đóng modal **trên cùng**. Trước đây mỗi modal tự nghe `keydown` trên
 *    `window`, nên bấm Escape trong hộp "Chọn một ảnh" là đóng luôn form "Thêm danh mục"
 *    bên dưới — mất sạch những gì đã gõ.
 * 2. `document.body.style.overflow` chỉ có MỘT giá trị. Modal trong cùng đóng lại mà trả
 *    ngay về `''` thì nền cuộn được trong khi modal ngoài vẫn đang mở.
 */
const stack: symbol[] = []

export function Modal({
  open, onClose, title, children, footer, wide,
}: { open: boolean; onClose: () => void; title: string; children: React.ReactNode; footer?: React.ReactNode; wide?: boolean }) {
  /**
   * `onClose` là arrow mới ở mọi call site → để trong deps là gỡ/gắn listener mỗi lần
   * render. Cùng lý do với `fetcher` của `useList` (`CLAUDE.md` §7).
   */
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const idRef = useRef<symbol>(null)
  idRef.current ??= Symbol('modal')

  useEffect(() => {
    if (!open) return
    const id = idRef.current!
    stack.push(id)
    document.body.style.overflow = 'hidden'

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || stack.at(-1) !== id) return
      onCloseRef.current()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      const i = stack.lastIndexOf(id)
      if (i !== -1) stack.splice(i, 1)
      if (!stack.length) document.body.style.overflow = ''
    }
  }, [open])

  if (!open) return null
  /**
   * Portal ra `body` — **không** render tại chỗ.
   *
   * Overlay là `position: fixed` nên trông thì ở đâu cũng vậy, nhưng vị trí trong CÂY DOM
   * thì có hậu quả thật. `Field` bọc con nó trong `<label>` (`Field.tsx`), nên khi
   * `ImageField` nằm trong `Field` thì cả hộp "Chọn một ảnh" là con cháu của cái `<label>`
   * đó. `<label>` không có `for` sẽ chuyển tiếp click sang phần tử labelable đầu tiên —
   * chính là nút "Chọn ảnh". Bấm một ô ảnh → React gỡ hộp thoại khỏi DOM ngay trong lượt
   * dispatch → tới lượt xử lý mặc định của `<label>` thì ô ảnh vừa bấm không còn là con
   * cháu của label nữa, nên ngoại lệ "click vào interactive content thì bỏ qua" không áp
   * dụng → label bấm hộ nút "Chọn ảnh" → **hộp thoại mở lại**. Đo được ở
   * `/categories` (xem `docs/DECISIONS.md`).
   *
   * Portal cũng gỡ luôn hai chuyện: overlay bị kẹt trong `overflow-y-auto` của modal cha,
   * và tranh chấp thứ tự chồng lớp.
   */
  return createPortal(
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-ink/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={title} className={`flex max-h-[90vh] w-full flex-col rounded-card bg-white shadow-float ${wide ? 'max-w-5xl' : 'max-w-lg'}`}>
        <div className="flex items-center justify-between border-b border-border px-5 py-3.5">
          <h2 className="text-h2">{title}</h2>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Đóng">✕</Button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="flex justify-end gap-2 border-t border-border px-5 py-3.5">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}

/** Hộp xác nhận cho hành động phá huỷ. Luôn nói rõ **cái gì** sẽ bị xoá. */
export function ConfirmDialog({
  open, onCancel, onConfirm, title, message, confirmLabel = 'Xoá', loading,
}: { open: boolean; onCancel: () => void; onConfirm: () => void; title: string; message: React.ReactNode; confirmLabel?: string; loading?: boolean }) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      title={title}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>Huỷ</Button>
          <Button variant="danger" onClick={onConfirm} loading={loading}>{confirmLabel}</Button>
        </>
      }
    >
      <div className="text-sm text-ink-soft">{message}</div>
    </Modal>
  )
}
